import {
  cancelScheduledDriveMutation,
  getAllActiveRoutesOptions,
  getAllVehiclesOptions,
  updateScheduledDriveMutation,
  getAllBillingPeriodsOptions,
  getRequirementSubintervalsQueryKey,
  getRequirementSubintervalsOptions,
} from '@/api/main'
import { Modal, Stack, Button, Text, Grid, Group } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useQueryClient, useSuspenseQuery, useMutation, useSuspenseQueries } from '@tanstack/react-query'
import { isEqual } from 'lodash-es'
import { useMemo, useState } from 'react'
import { DriveFormValues, DriveFormModal } from './DriveFormModal'
import { useRescheduleDrive } from './hooks/useSchedulerMutations'
import {
  DriveBillingType,
  RequirementSubintervalsResponse,
  ScheduledDriveResponseWithEndpoints,
} from '@/api/main/types.gen'
import DriveOriginIframe from './DriveOriginIframe'
import { secondsToHHMM } from '@/utils/datetime'
import { aggregateRequirementSubintervals, getOverlappingBillingPeriods } from '@/utils/scheduling'
import { buildEditRescheduleBody } from './editDriveReschedule'
import { useTranslation } from 'react-i18next'

function EditDriveModalContent({
  drive,
  billingPeriodId,
  startDate,
  endDate,
  onClose,
}: {
  drive: ScheduledDriveResponseWithEndpoints
  billingPeriodId: number
  startDate: string
  endDate: string
  onClose: () => void
}) {
  const { t } = useTranslation(['scheduling', 'common'])
  const queryClient = useQueryClient()
  const { data: routes } = useSuspenseQuery(getAllActiveRoutesOptions({}))
  const { data: vehicles } = useSuspenseQuery(getAllVehiclesOptions())
  const { data: billingPeriods } = useSuspenseQuery(getAllBillingPeriodsOptions())

  // Decode dates for calculations
  const decodedStartDate = decodeURIComponent(startDate)
  const decodedEndDate = decodeURIComponent(endDate)

  // Calculate overlapping billing periods
  const overlappingPeriods = useMemo(
    () => getOverlappingBillingPeriods(decodedStartDate, decodedEndDate, billingPeriods),
    [decodedStartDate, decodedEndDate, billingPeriods]
  )
  const requirementSubintervalsQueries = useMemo(
    () =>
      overlappingPeriods.map(period =>
        getRequirementSubintervalsOptions({
          path: {
            billing_period_id: period.id,
            starting_from: decodedStartDate,
            ending_at: decodedEndDate,
          },
        })
      ),
    [overlappingPeriods, decodedStartDate, decodedEndDate]
  )
  const requirementSubintervalsResults = useSuspenseQueries({
    queries: requirementSubintervalsQueries,
    combine: results => results.map(result => result.data as RequirementSubintervalsResponse),
  })
  const requirementSubintervals = useMemo(
    () => aggregateRequirementSubintervals(requirementSubintervalsResults),
    [requirementSubintervalsResults]
  )

  // Create driveIntervalsQueryKey for useRescheduleDrive hook
  const driveIntervalsQueryKey = useMemo(
    () => ({
      path: {
        billing_period_id: billingPeriodId,
        starting_from: decodedStartDate,
        ending_at: decodedEndDate,
      },
    }),
    [billingPeriodId, decodedStartDate, decodedEndDate]
  )

  const { mutate: updateDrive, isPending: isUpdatingDrive } = useMutation(updateScheduledDriveMutation())
  const { mutate: rescheduleDrive, isPending: isReschedulingDrive } = useRescheduleDrive(driveIntervalsQueryKey, {
    overlappingPeriods,
  })

  const { mutate: cancelDrive, isPending: isCancellingDrive } = useMutation({
    ...cancelScheduledDriveMutation(),
    onSuccess: () => {
      showNotification({ message: t('driveCancelled') })
      overlappingPeriods.forEach(period => {
        const queryKey = getRequirementSubintervalsQueryKey({
          path: {
            billing_period_id: period.id,
            starting_from: decodedStartDate,
            ending_at: decodedEndDate,
          },
        })
        queryClient.invalidateQueries({ queryKey })
      })
      onClose()
    },
    onError: () => {
      showNotification({ message: t('errorCancellingDrive'), color: 'red' })
    },
  })

  const isPending = isUpdatingDrive || isReschedulingDrive || isCancellingDrive
  const driveId = drive.id
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)

  const handleCancelDrive = () => {
    setShowCancelConfirmation(true)
  }

  const handleConfirmCancel = () => {
    cancelDrive({
      path: {
        drive_id: driveId,
      },
    })
  }

  const handleCancelConfirmationClose = () => {
    setShowCancelConfirmation(false)
  }

  const handleSubmit = (values: DriveFormValues) => {
    const vehicleIdChanged = drive.vehicle_id !== values.vehicle_id
    const startAfterChanged = !isEqual(new Date(drive.start_after), new Date(values.start_after))
    const finishBeforeChanged = !isEqual(new Date(drive.finish_before), new Date(values.finish_before))
    const billingTypeChanged = drive.billing_type !== values.billing_type

    if (vehicleIdChanged || startAfterChanged || finishBeforeChanged) {
      const rescheduleBody = buildEditRescheduleBody({
        driveId: Number(driveId),
        fallbackVehicleId: drive.vehicle_id,
        values,
        driveSubintervals: requirementSubintervals.drive_subintervals,
      })
      rescheduleDrive(
        {
          body: rescheduleBody,
        },
        {
          onSuccess: () => {
            const routeChanged = drive.route_id !== values.route_id || drive.route_version !== values.route_version
            const driveTypeChanged =
              JSON.stringify([...drive.drive_type].sort()) !== JSON.stringify([...values.drive_type].sort())

            if (routeChanged || driveTypeChanged || billingTypeChanged) {
              updateDrive(
                {
                  path: { drive_id: Number(driveId) },
                  body: {
                    route_id: values.route_id,
                    route_version: values.route_version,
                    drive_type: values.drive_type,
                    billing_type: values.billing_type as DriveBillingType,
                  },
                },
                {
                  onSuccess: () => {
                    showNotification({ message: t('driveRescheduledAndUpdatedSuccessfully'), color: 'green' })
                    onClose()
                  },
                  onError: () => {
                    showNotification({ message: t('driveRescheduledButFailedToUpdateMetadata'), color: 'orange' })
                    onClose()
                  },
                }
              )
            } else {
              showNotification({ message: t('driveRescheduledSuccessfully'), color: 'green' })
              onClose()
            }
          },
          onError: () => {
            showNotification({ message: t('failedToRescheduleDrive'), color: 'red' })
          },
        }
      )
    } else {
      updateDrive(
        {
          path: { drive_id: Number(driveId) },
          body: {
            route_id: values.route_id,
            route_version: values.route_version,
            drive_type: values.drive_type,
            billing_type: values.billing_type as DriveBillingType,
          },
        },
        {
          onSuccess: () => {
            showNotification({ message: t('driveUpdatedSuccessfully'), color: 'green' })
            // Invalidate all requirementSubintervals queries for overlapping periods
            overlappingPeriods.forEach(period => {
              const queryKey = getRequirementSubintervalsQueryKey({
                path: {
                  billing_period_id: period.id,
                  starting_from: decodedStartDate,
                  ending_at: decodedEndDate,
                },
              })
              queryClient.invalidateQueries({ queryKey })
            })
            onClose()
          },
          onError: () => {
            showNotification({ message: t('failedToUpdateDrive'), color: 'red' })
          },
        }
      )
    }
  }

  const title = showCancelConfirmation
    ? t('cancelDrive')
    : t('editDriveFor', { driveId: drive.id, vehicleId: drive.vehicle_id.toUpperCase() })

  const handleModalClose = () => {
    if (showCancelConfirmation) {
      setShowCancelConfirmation(false)
    } else {
      onClose()
    }
  }

  return (
    <Modal opened={true} onClose={handleModalClose} title={title} size={showCancelConfirmation ? 'md' : 'lg'}>
      {showCancelConfirmation ? (
        <>
          <Text mb="md">{t('confirmCancelDrive', { driveId })}</Text>
          <Text mb="md" bg="yellow" p="md" styles={{ root: { borderRadius: '4px' } }}>
            <strong>{t('warning', { ns: 'common' })}:</strong> {t('driveDeletedAfterCancellation')}
          </Text>
          <Group justify="flex-end" mt="md">
            <Button onClick={handleCancelConfirmationClose}>{t('keepDrive')}</Button>
            <Button color="red" onClick={handleConfirmCancel} loading={isCancellingDrive}>
              {t('cancelDrive')}
            </Button>
          </Group>
        </>
      ) : (
        <Grid>
          <Grid.Col span={6}>
            <DriveFormModal
              modalKey={`edit-drive-form-${driveId}`}
              routes={routes}
              vehicles={vehicles}
              initialValues={{
                route_id: drive.route_id,
                route_version: drive.route_version || '',
                drive_type: drive.drive_type || [],
                vehicle_id: drive.vehicle_id,
                start_after: drive.start_after,
                finish_before: drive.finish_before,
                billing_type: drive.billing_type || '',
              }}
              onSubmit={handleSubmit}
              isLoading={isPending}
            >
              <Stack gap="md" mt="md">
                <Button type="submit" form="drive-form" loading={isPending} fullWidth>
                  {t('updateDrive')}
                </Button>
                <Button
                  color="red"
                  onClick={handleCancelDrive}
                  loading={isCancellingDrive}
                  disabled={isPending && !isCancellingDrive}
                  fullWidth
                >
                  {t('cancelDrive')}
                </Button>
              </Stack>
            </DriveFormModal>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text fz={14} fw={500} mb="xs">
              {t('origin')}
            </Text>
            <DriveOriginIframe latitude={drive.origin_point?.latitude} longitude={drive.origin_point?.longitude} />
            <Text fz={14} fw={500} mb="xs">
              {t('destination')}
            </Text>
            <DriveOriginIframe
              latitude={drive.destination_point?.latitude}
              longitude={drive.destination_point?.longitude}
            />
            <Text fz={14} fw={500} mb="xs">
              {t('estimatedDuration')}: {secondsToHHMM(drive.estimated_duration ?? 0)}
            </Text>
          </Grid.Col>
        </Grid>
      )}
    </Modal>
  )
}

export default EditDriveModalContent
